import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { congesApi } from '../../services/conges-api';
import { MessageErreur } from '../../components/ndf/ui/MessageEtat';
import { COULEUR_STATUT, LIBELLE_STATUT } from './CongesDemandesPage';
import { periodeFr } from '../../lib/periode-conges';

/**
 * Les demandes qui attendent une décision de moi (R-VAL-10).
 *
 * Triées par ancienneté de soumission : ce qui attend depuis le plus longtemps
 * se traite d'abord, et c'est aussi ce qui bloque quelqu'un.
 *
 * La sélection vient du serveur, du MÊME service que le badge de comptage :
 * deux règles séparées donneraient deux chiffres, et c'est toujours celui qu'on
 * ne regarde pas qui a raison.
 */

/** Depuis combien de jours cette demande attend-elle ? */
function attenteEnJours(soumiseAt: string | null): number | null {
  if (!soumiseAt) return null;
  const ms = Date.now() - new Date(soumiseAt).getTime();
  return Math.floor(ms / (24 * 3600 * 1000));
}

export function CongesAValiderPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['conges', 'a-valider'],
    queryFn: () => congesApi.demandes.aValider(),
  });

  if (isLoading) return <p className="p-6 text-nuit/60">Chargement…</p>;
  if (error) return <div className="p-6"><MessageErreur erreur={error} /></div>;

  const demandes = data?.demandes ?? [];

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4 md:p-6">
      <header>
        <h1 className="font-titre text-xl font-bold text-nuit">Absences à valider</h1>
        <p className="text-sm text-nuit/60">
          {demandes.length === 0
            ? 'Rien n’attend votre décision.'
            : `${demandes.length} demande(s) attendent votre décision.`}
        </p>
      </header>

      {demandes.length > 0 && (
        <ul className="space-y-2">
          {demandes.map((d) => {
            const jours = attenteEnJours(d.soumise_at);
            return (
              <li key={d.id}>
                <Link
                  to={`/conges/demandes/${d.id}?depuis=/conges/a-valider`}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white p-3 transition-colors hover:bg-ciel/10"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-nuit">
                      {d.salarie_nom} <span className="font-normal text-nuit/60">({d.type_libelle})</span>
                    </p>
                    <p className="text-sm text-nuit/60">
                      {periodeFr(d)} ({d.nb_jours} j)
                      {d.description ? ` (${d.description})` : ''}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Ce qui demande l'attention saute aux yeux. */}
                    {d.a_posteriori && (
                      <span className="rounded-full bg-jaune-fond/40 px-2 py-0.5 text-xs font-medium text-jaune">
                        a posteriori
                      </span>
                    )}
                    {d.demande_liee_id && (
                      <span className="rounded-full bg-violet-fond/30 px-2 py-0.5 text-xs font-medium text-violet">
                        demande liée
                      </span>
                    )}
                    {jours !== null && jours >= 5 && (
                      <span className="rounded-full bg-corail-fond/40 px-2 py-0.5 text-xs font-medium text-corail">
                        attend depuis {jours} j
                      </span>
                    )}
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        COULEUR_STATUT[d.statut] ?? 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      {LIBELLE_STATUT[d.statut] ?? d.statut}
                    </span>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
